<template>
  <InfoCard
    :saveButtonText="saveButtonText"
    @saveChanges="handleSaveChanges"
  >
    <div class="product-safety-content additional-info">
      <div class="title-area">
        <div class="title">Ürün Besin Bilgileri</div>
        <div class="description">
          Ürün besin bilgileri, gıda ürünlerine dair özellik ve uyarıları içerir.
        </div>
      </div>
      <div class="product-safety-content-boxes" :class="{ disabled: isProductDisabled }">
        <Input
          type="textarea"
          label="Alerjen Uyarısı"
          id="allergen-warning"
          v-model:value="formData.allergenWarning"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Muhafaza Koşulları"
          id="storage-conditions"
          v-model:value="formData.storageConditions"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Kullanım Önerileri"
          id="usage-recommendations"
          v-model:value="formData.usageRecommendations"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="İçindekiler"
          id="contents"
          v-model:value="formData.contents"
          :disabled="isProductDisabled"
        />
        <DatePickerV2
          class="datePicker last-consumption-date"
          title="Son Tüketim Tarihi"
          @selectedValue="(value) => setSelectedCustomDate(value, 'expiration')"
          :value="formData.expirationDate"
          width="100%"
          :disable="isProductDisabled"
        />
        <DatePickerV2
          class="datePicker recommended-consumption-date"
          title="Tavsiye Edilen Tüketim Tarihi"
          @selectedValue="(value) => setSelectedCustomDate(value, 'recommended')"
          :value="formData.recommendedConsumptionDate"
          width="100%"
          :disable="isProductDisabled"
        />
      </div>
    </div>
  </InfoCard>
</template>

<script setup>
import { reactive, watch, ref, computed } from "vue";
import { useStore } from "vuex";
import { DatePickerV2, Input } from "@n11/so-ui-components";
import InfoCard from "../../partial/ProductSafetyComponent/InfoCard.vue";
import { formatDateToString, parseDateString } from "@/utils/formatedDate";

const store = useStore();
const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const props = defineProps({
  nutritionInfo: {
    type: Object,
    default: () => null
  },
  isSaved: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['hasChanges', 'saveChanges']);

const formData = reactive({
  allergenWarning: "",
  storageConditions: "",
  usageRecommendations: "",
  contents: "",
  expirationDate: null,
  recommendedConsumptionDate: null,
});

const isInitialized = ref(false);

const saveButtonText = computed(() => {
  return props.isSaved ? 'Değişiklikler Kaydedildi' : 'Değişiklikleri Kaydet'
});

watch(() => props.nutritionInfo, (newValue) => {
  
  if (newValue) {
    formData.allergenWarning = newValue.allergenWarning || "";
    formData.storageConditions = newValue.storageConditions || "";
    formData.usageRecommendations = newValue.usageRecommendations || "";
    formData.contents = newValue.contents || "";
    formData.expirationDate = parseDateString(newValue.expirationDate);
    formData.recommendedConsumptionDate = parseDateString(newValue.recommendedConsumptionDate);
    isInitialized.value = true;
  } else {
    isInitialized.value = true;
  }
}, { immediate: true });

const setSelectedCustomDate = (value, type) => {
  if (type === 'expiration') {
    formData.expirationDate = value;
  } else if (type === 'recommended') {
    formData.recommendedConsumptionDate = value;
  }
  if (isInitialized.value) {
    emit('hasChanges');
  }
};

watch(
  () => [
    formData.allergenWarning,
    formData.storageConditions,
    formData.usageRecommendations,
    formData.contents,
    formData.expirationDate,
    formData.recommendedConsumptionDate,
  ],
  () => {
    if (isInitialized.value) {
      emit('hasChanges');
    }
  }
);

const handleSaveChanges = async () => {
  emit('saveChanges');
};

const getData = () => {
  return {
    allergenWarning: formData.allergenWarning || null,
    storageConditions: formData.storageConditions || null,
    usageRecommendations: formData.usageRecommendations || null,
    contents: formData.contents || null,
    expirationDate: formatDateToString(formData.expirationDate),
    recommendedConsumptionDate: formatDateToString(formData.recommendedConsumptionDate),
  };
};

defineExpose({ getData });
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.product-safety-content,
.box {
  .title-area {
    .title {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      margin-bottom: 8px;
    }

    .description {
      font-size: 11px;
      line-height: 14px;
      height: 30px;
    }
  }
  &-form-area {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 16px 0px;
    padding: 24px;
    border-radius: 8px;
    border: 1px solid $colorM10;
    background-color: #f8f7fc;
  }
}
.product-safety-content {
  &-boxes {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    > * {
      flex: 1;
      min-width: 0;
    }

    .datePicker {
      width: 100%;
    }

    :deep(.datePicker .dp__input__wrapper),
    :deep(.datePicker .dp__input) {
      width: 100%;
      min-width: 0;
    }

    .last-consumption-date,
    .recommended-consumption-date {
      --input-height: 40px;

      :deep(.dp__input__wrapper) {
        height: var(--input-height);
        color: $colorN80;
        padding: 8px 12px 8px 16px;
        position: relative;
      }

      :deep(.dp__input) {
        height: 100%;
        color: $colorN80;
        font-size: 12px;
        width: 100%;
      }

      :deep(.dp__input__wrapper)::before,
      :deep(.dp__input__wrapper)::after {
        position: absolute;
        right: 12px;
        left: auto;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
      }

      :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown))),
      :deep(:has(.dp__container) .dp__input__wrapper) {
        padding: 18px 12px 8px 16px;
      }
    }

    .last-consumption-date {
      :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown)))::before,
      :deep(:has(.dp__container) .dp__input__wrapper)::before {
        content: "Son Tüketim Tarihi";
        position: absolute;
        left: 16px;
        right: auto;
        top: 4px;
        transform: none;
        font-size: 9px;
        color: $colorD40;
      }
    }

    .recommended-consumption-date {
      :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown)))::before,
      :deep(:has(.dp__container) .dp__input__wrapper)::before {
        content: "Tavsiye Edilen Tüketim Tarihi";
        position: absolute;
        left: 16px;
        right: auto;
        top: 4px;
        transform: none;
        font-size: 9px;
        color: $colorD40;
      }
    }

    &.disabled {
      :deep(.datePicker .dp__input__wrapper.has-value) {
        cursor: not-allowed;
        pointer-events: none;
        background-color: #f6f7f9;
      }
    }
  }
}
.error {
  &-text {
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
    color: $colorE50;
    margin-left: 12px;
  }
}
</style>
